import { extractMolEvent } from '@moleculeone/draw';
import { Compound } from '@moleculeone/ui';
import React, { useState } from 'react';

import { DrawingSpace, PeriodicTable, ToolbarMinimal } from '../../BaseEditor/components';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuTrigger,
} from '../../ContextMenu/ContextMenu';
import { keyframes, styled } from '../../../../stitches.config';
import { getContextMenuItems } from '../../BaseEditor/etc/getContextMenuItems';
import { smilesToMol } from '../../BaseEditor/utils/conversion';
import { useKeyboard } from '../../BaseEditor/hooks/useKeyboard';
import { Space, MolEvent, CompoundEditorActions } from '../../BaseEditor/hooks/useCompoundEditor';
import { Button } from '../../Button/Button';
import { Skeleton } from '../../../layout/Skeleton/Skeleton';

const Container = styled('div', {
  'height': '100%',
  'flex': 1,
  'overflow': 'hidden',
  'backgroundColor': '$slate3',
  'borderRadius': '$medium',
  'position': 'relative',
  'margin': '0 $4',
  '& .atom-bg': { fill: '$slate3' },
});

const CompoundToolbar = styled(ToolbarMinimal, {
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: 1,
});

const drawingSpaceChoicesOpen = keyframes({
  '0%': { opacity: 1, height: '100%', transform: 'translateY(0)' },
  '45%': {
    opacity: 0,
    height: '100%',
    transform: 'translateY(-1rem)',
  },
  '55%': { opacity: 0, height: '34%', transform: 'translateY(1rem)' },
  '100%': { opacity: 1, height: '34%', transform: 'translateY(0)' },
});
const DrawingSpaceContainer = styled('div', {
  'height': '100%',
  'padding': '$5',
  'mixBlendMode': 'darken',
  '&[data-choices-open=true]': {
    animation: `${drawingSpaceChoicesOpen} .4s both`,
  },
});

const CompoundChoicesContainer = styled('div', {
  'position': 'absolute',
  'left': 0,
  'right': 0,
  'bottom': 0,
  'height': '66%',
  'backgroundColor': '$slate3',
  'opacity': 0,
  'pointerEvents': 'none',
  'transform': 'translateY(100%)',
  'transition': 'opacity .3s, transform .5s cubic-bezier(0.16, 1, 0.3, 1)',
  '&[data-open=true]': {
    transform: 'translateY(0)',
  },
  '&[data-visible=true]': {
    opacity: 1,
    pointerEvents: 'auto',
  },
});
const CompoundChoicesButton = styled(Button, {
  position: 'absolute',
  top: 0,
  right: 0,
  transform: 'translateY(-100%)',
});
const CompoundChoices = styled('div', {
  overflow: 'auto',
});
const CompoundChoice = styled('button', {
  'display': 'flex',
  'justifyContent': 'center',
  'width': '100%',
  'border': 'none',
  'background': 'none',
  'cursor': 'pointer',
  '&:not(:last-child)': {
    borderBottom: '1px solid $slate6',
  },
  '&:hover, &:focus': {
    backgroundColor: '$slateA2',
  },
});
const CompoundChoiceImage = styled('div', {
  'pointerEvents': 'none',
  'height': '8rem',
  'padding': '$5',
  '> svg': {
    height: '100%',
  },
});

type ReactionEditorCompoundProps = {
  loading: boolean;
  spaceIdx: number;
  compound: Space;
  compoundChoices?: string[];
  minimal: boolean;
  actions: CompoundEditorActions;
  smiles: string;
  context?: React.ReactNode;
};

export const ReactionEditorCompound = ({
  loading,
  spaceIdx,
  compound,
  compoundChoices,
  minimal,
  actions,
  smiles,
  context,
}: ReactionEditorCompoundProps) => {
  const [event, setEvent] = useState<MolEvent>({ empty: true });
  const [choicesOpen, setChoicesOpen] = useState(false);

  useKeyboard({
    onKeyDown: (event: any) => {
      if (event.key === 'Escape') {
        setChoicesOpen(false);
      }
    },
  });

  if (loading) {
    return (
      <Skeleton
        css={{
          height: '100%',
          flex: 1,
          backgroundColor: '$slate3',
          borderRadius: '$medium',
          margin: '0 $4',
        }}
      />
    );
  }

  const onContextMenuOpen = (event: any) => {
    setEvent(extractMolEvent(event));
  };

  const onPickAtom = (mol: any) => {
    actions.onPickElement(event, mol.atoms[0].element);
  };

  const selectChoice = (choice: string) => async () => {
    const mol = await smilesToMol(choice);
    actions.setMol(mol, String(spaceIdx));
    setChoicesOpen(false);
  };

  const menuItems = getContextMenuItems({
    event,
    compound,
    actions,
  });

  return (
    <ContextMenu>
      <Container>
        {!minimal && <CompoundToolbar clear={compound.clear} redraw={compound.redraw} smiles={smiles} />}

        <ContextMenuTrigger asChild>
          <DrawingSpaceContainer data-choices-open={choicesOpen}>
            <DrawingSpace onContextMenuOpen={onContextMenuOpen} {...compound} />
          </DrawingSpaceContainer>
        </ContextMenuTrigger>

        {compoundChoices && compoundChoices.length > 0 && (
          <CompoundChoicesContainer data-open={choicesOpen} data-visible={!minimal}>
            <CompoundChoicesButton
              weight="minimal"
              color="slate"
              icon={`filter_${compoundChoices.length <= 9 ? compoundChoices.length : '9_plus'}`}
              active={choicesOpen}
              onClick={() => {
                setChoicesOpen(!choicesOpen);
              }}
              css={{
                fontSize: '$4',
                borderRadius: '$medium 0 0 0',
              }}
            />
            <CompoundChoices>
              {compoundChoices.map(choice => (
                <CompoundChoice onClick={selectChoice(choice)} key={choice}>
                  <CompoundChoiceImage>
                    <Compound smiles={choice} />
                  </CompoundChoiceImage>
                </CompoundChoice>
              ))}
            </CompoundChoices>
          </CompoundChoicesContainer>
        )}

        {context}
      </Container>

      <ContextMenuContent alignOffset={5}>
        {menuItems.map(({ groupLabel, items }, idx) => (
          <React.Fragment key={idx}>
            {groupLabel && items.length > 0 && <ContextMenuLabel>{groupLabel}</ContextMenuLabel>}
            {items.map(([action, label]) =>
              action === 'pick-element' ? (
                <ContextMenu key={label}>
                  <ContextMenuTrigger>{label}</ContextMenuTrigger>
                  <ContextMenuContent alignOffset={3} css={{ padding: '$4' }}>
                    <PeriodicTable onPickAtom={onPickAtom} />
                  </ContextMenuContent>
                </ContextMenu>
              ) : (
                <ContextMenuItem key={label} onClick={action}>
                  {label}
                </ContextMenuItem>
              )
            )}
          </React.Fragment>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
};
